import React from 'react';
import { Check, Minus } from 'lucide-react';
import { cn } from '../../utils/cn';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { formatPrice } from '../../utils/formatPrice';
import { Plan, PaidTier, DurationMonths, getPlanPrice } from '../../config/plans';

export interface PlanCardProps {
  plan: Plan;
  months?: DurationMonths;
  highlighted?: boolean;
  current?: boolean;
  cta?: React.ReactNode;
  compact?: boolean;
  className?: string;
}

/** Plan card used by landing pricing, Upgrade and UpgradeModal. */
export const PlanCard: React.FC<PlanCardProps> = ({
  plan,
  months = 12,
  highlighted = false,
  current = false,
  cta,
  compact = false,
  className,
}) => {
  const isFree = plan.tier === 'free';
  const price = isFree ? null : getPlanPrice(plan.tier as PaidTier, months);

  return (
    <Card
      padding={compact ? 'sm' : 'md'}
      className={cn('pricing-card flex flex-col gap-4', highlighted && 'border-2 border-brand', className)}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="pricing-plan font-display text-lg font-semibold text-ink">{plan.name}</h3>
          <p className="text-sm text-muted">{plan.tagline}</p>
        </div>
        {current && <Badge tone="brand">Dein Plan</Badge>}
      </div>

      <div className="flex items-baseline gap-2 tabular">
        {isFree || !price ? (
          <>
            <span className="text-3xl font-semibold text-ink">{formatPrice(0)}</span>
            <span className="text-sm text-muted">14 Tage</span>
          </>
        ) : (
          <>
            <span className="text-3xl font-semibold text-ink">{formatPrice(price.totalCents)}</span>
            {months > 1 && (
              <span className="text-sm text-muted">{formatPrice(price.perMonthCents, { perMonth: true })}</span>
            )}
          </>
        )}
      </div>

      <ul className="flex flex-col gap-2">
        {plan.features.map((feature) => (
          <li key={feature.label} className="flex items-start gap-2 text-sm">
            {feature.included ? (
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-success-text" aria-hidden="true" />
            ) : (
              <Minus className="mt-0.5 h-4 w-4 shrink-0 text-muted" aria-hidden="true" />
            )}
            <span className={feature.included ? 'text-ink' : 'text-muted'}>
              {feature.label}
              {feature.note && <span className="text-muted"> ({feature.note})</span>}
            </span>
          </li>
        ))}
      </ul>

      {cta}
    </Card>
  );
};
